import { z } from 'zod'

import { searchTextParamSchema } from '@/schemas/common'
import { calendarDateSchema } from '@/schemas/portfolio'
import { BREAKOUT_DIRECTIONS, CYCLE_GROUPINGS, SIGNAL_SIDES } from '@/schemas/signals'

/**
 * URL building blocks shared by the Signal Zone routes. Each route's own
 * schema lives in its `<Screen>.loader.ts` and doubles as its `validateSearch`
 * (docs/route-loading.md, "Search params"), so the loader and the screen read
 * one URL one way.
 *
 * EVERY FIELD FALLS BACK instead of throwing (`.catch`), the rule
 * `screens/mutual-funds/search.ts` states: these are filters and pages over
 * one screen, so a hand-edited or stale value lands on the default view.
 *
 * No `createFileRoute` here, so a test may import this module.
 */

/**
 * A session, `YYYY-MM-DD`. Absent means mm-be's latest computed session (its
 * own default). A malformed or impossible one falls back to that too, rather
 * than reaching mm-be as a 400 that "Try again" would repeat forever; a real
 * day with nothing computed is the screen's empty state.
 *
 * On every Signals page the picker sits on. Where the read takes a date (Dashboard, Daily,
 * Trend, Steady, Breakouts) it is the read's `date`; History, Volatile, Open trades, Cycle map
 * and Momentum ignore it, so a past one is the screen's no-data state (`PastSessionEmpty`).
 */
export const sessionDateParamSchema = calendarDateSchema.optional().catch(undefined)

/**
 * Which board a Live or Daily page shows, one at a time (underline tabs,
 * owner 2026-10-12: the All option went). Green opens; a Dashboard count card
 * links with `?side=`, and an old `?side=all` link lands on green too.
 */
export const sideParamSchema = z.enum(SIGNAL_SIDES).default('green').catch('green')

/**
 * A live run, by the id `/live/runs/` gave it. Absent means "All signals": every
 * stock that signalled at any run, which also keeps polling. A malformed id
 * lands there too; a well-formed one mm-be no longer has is the screen's
 * region-local failure, not a throw here.
 */
export const liveRunParamSchema = z.number().int().min(1).optional().catch(undefined)

/** A symbol prefix, as `/trend/` and `/history/` take it: trimmed, ≤ 20 characters. */
export const symbolSearchParamSchema = searchTextParamSchema
  .transform((text) => text.trim().slice(0, 20))
  .pipe(z.string().min(1))
  .optional()
  .catch(undefined)

export const breakoutDirectionParamSchema = z
  .enum(BREAKOUT_DIRECTIONS)
  .default('above')
  .catch('above')

export const cycleGroupingParamSchema = z.enum(CYCLE_GROUPINGS).default('month').catch('month')

/** mm-be's 1–10 BS years; 5 is its default and the design's "last five years". */
export const cycleYearsParamSchema = z.number().int().min(1).max(10).default(5).catch(5)

/**
 * "Rows per page" on every Signal Zone list, 15 first (owner 2026-10-12: the
 * frames open at 15). Its own set rather than the app's 10/20/50/100
 * (`schemas/table.ts`), which has no 15; every size is within the signals
 * endpoints' `page_size` ceiling of 100. Hand it to `Pagination`'s
 * `pageSizes`, which `SignalsTableFrame` does.
 */
export const SIGNAL_PAGE_SIZES = [15, 30, 50, 100] as const
export type SignalPageSize = (typeof SIGNAL_PAGE_SIZES)[number]
export const SIGNAL_DEFAULT_PAGE_SIZE: SignalPageSize = 15

const signalPageSizeSchema = z.literal(SIGNAL_PAGE_SIZES)

/**
 * A pager's plain `number` narrowed to an offered size, else `fallback`: the
 * menu cannot produce another, so this guards a search param, it is not a
 * path a member can reach (`toPageSize`'s rule, on this set).
 */
export const toSignalPageSize = (next: number, fallback: SignalPageSize): SignalPageSize => {
  const parsed = signalPageSizeSchema.safeParse(next)
  return parsed.success ? parsed.data : fallback
}

/**
 * Paging over `?page=` and `?pageSize=`, server-side or the client's own,
 * opening at 15. A size outside the set — an old link's 10 or 20 — lands on
 * 15 rather than throwing.
 */
export const pagingParams = () => ({
  page: z.number().int().min(1).default(1).catch(1),
  pageSize: signalPageSizeSchema.default(SIGNAL_DEFAULT_PAGE_SIZE).catch(SIGNAL_DEFAULT_PAGE_SIZE),
})
